import { blockCategories } from "../blocks/categories";

const COMPONENT_TYPE = 'sio-condition-block';
const BLOCK_ID = 'sio-condition-block';
const DEFAULT_OPERATOR = 'isNotEmpty';

export const CONDITION_OPERATORS = [
    { value: 'isEmpty', name: 'Ist leer' },
    { value: 'isNotEmpty', name: 'Ist nicht leer' },
    { value: 'equals', name: 'Gleich (=)' },
    { value: 'notEqual', name: 'Ungleich (≠)' },
    { value: 'gt', name: 'Größer als (>)' },
    { value: 'gte', name: 'Größer oder gleich (≥)' },
    { value: 'lt', name: 'Kleiner als (<)' },
    { value: 'lte', name: 'Kleiner oder gleich (≤)' },
    { value: 'contains', name: 'Enthält' },
    { value: 'notContains', name: 'Enthält nicht' },
];

const OPERATORS_WITHOUT_VALUE = new Set(['isEmpty', 'isNotEmpty']);

const getFieldLabel = (fieldValue, fieldOptions = []) => {
    const option = fieldOptions.find((entry) => entry.value === fieldValue);
    return option?.name ?? fieldValue;
};

const getOperatorLabel = (operator) => {
    const option = CONDITION_OPERATORS.find((entry) => entry.value === operator);
    return option?.name ?? operator;
};

const buildPreviewLabel = (field, operator, compareValue, fieldOptions) => {
    const fieldLabel = getFieldLabel(field, fieldOptions);
    const operatorLabel = getOperatorLabel(operator);
    if (OPERATORS_WITHOUT_VALUE.has(operator)) {
        return `Wenn: ${fieldLabel} ${operatorLabel}`;
    }
    const valueLabel = compareValue ? String(compareValue) : '…';
    return `Wenn: ${fieldLabel} ${operatorLabel} ${valueLabel}`;
};

/**
 * Droppable teaser condition: children render on the frontend only when
 * the selected field matches the operator + value.
 */
export default function conditionBlock(
    editor,
    { fieldOptions = [], defaultField = 'title' } = {}
) {
    const blockManager = editor.BlockManager;
    const domComponents = editor.DomComponents;
    const defaultLabel = buildPreviewLabel(
        defaultField,
        DEFAULT_OPERATOR,
        '',
        fieldOptions
    );

    const fieldTrait = {
        type: 'select',
        name: 'conditionField',
        label: 'Feld',
        options: fieldOptions,
        changeProp: 1,
        default: defaultField,
    };

    const operatorTrait = {
        type: 'select',
        name: 'conditionOperator',
        label: 'Bedingung',
        options: CONDITION_OPERATORS,
        changeProp: 1,
        default: DEFAULT_OPERATOR,
    };

    const valueTrait = {
        type: 'text',
        name: 'conditionValue',
        label: 'Wert',
        placeholder: 'Vergleichswert',
        changeProp: 1,
        default: '',
    };

    const traitsForOperator = (operator) => {
        const traits = [fieldTrait, operatorTrait];
        if (!OPERATORS_WITHOUT_VALUE.has(operator)) {
            traits.push(valueTrait);
        }
        return traits;
    };

    domComponents.addType(COMPONENT_TYPE, {
        model: {
            defaults: {
                tagName: 'div',
                name: 'Bedingung',
                droppable: true,
                draggable: true,
                stylable: true,
                highlightable: true,
                attributes: {
                    class: 'sio-teaser-condition',
                    'data-siotype': COMPONENT_TYPE,
                    'data-condition-field': defaultField,
                    'data-condition-operator': DEFAULT_OPERATOR,
                    'data-condition-value': '',
                    'data-field-label': defaultLabel,
                },
                conditionField: defaultField,
                conditionOperator: DEFAULT_OPERATOR,
                conditionValue: '',
                traits: traitsForOperator(DEFAULT_OPERATOR),
                components: [],
            },

            init() {
                this.syncFromAttributes();
                this.on('change:conditionField', this.onConditionChange);
                this.on('change:conditionOperator', this.onOperatorChange);
                this.on('change:conditionValue', this.onConditionChange);
                this.on('change:attributes', this.onAttributesChange);
            },

            syncFromAttributes() {
                const attrs = this.getAttributes() || {};
                const field =
                    attrs['data-condition-field'] ||
                    this.get('conditionField') ||
                    defaultField;
                const operator =
                    attrs['data-condition-operator'] ||
                    this.get('conditionOperator') ||
                    DEFAULT_OPERATOR;
                const compareValue =
                    attrs['data-condition-value'] ??
                    this.get('conditionValue') ??
                    '';

                this.set('conditionField', field, { silent: true });
                this.set('conditionOperator', operator, { silent: true });
                this.set('conditionValue', compareValue, { silent: true });
                this.applyCondition({ field, operator, compareValue });
            },

            onAttributesChange() {
                const attrs = this.getAttributes() || {};
                const field = attrs['data-condition-field'];
                const operator = attrs['data-condition-operator'];
                const compareValue = attrs['data-condition-value'];
                if (
                    field !== this.get('conditionField') ||
                    operator !== this.get('conditionOperator') ||
                    compareValue !== this.get('conditionValue')
                ) {
                    this.syncFromAttributes();
                }
            },

            onOperatorChange() {
                const operator =
                    this.get('conditionOperator') || DEFAULT_OPERATOR;
                this.set(
                    'traits',
                    traitsForOperator(operator).map((trait) => ({
                        ...trait,
                        value: this.get(trait.name),
                    }))
                );
                this.onConditionChange();
            },

            onConditionChange() {
                this.applyCondition({
                    field: this.get('conditionField') || defaultField,
                    operator:
                        this.get('conditionOperator') || DEFAULT_OPERATOR,
                    compareValue: this.get('conditionValue') ?? '',
                });
            },

            applyCondition({ field, operator, compareValue }) {
                const label = buildPreviewLabel(
                    field,
                    operator,
                    compareValue,
                    fieldOptions
                );
                this.addAttributes({
                    'data-condition-field': field,
                    'data-condition-operator': operator,
                    'data-condition-value': compareValue ?? '',
                    'data-field-label': label,
                });
                this.set('name', label);
            },
        },

        view: {},
    });

    blockManager.add(BLOCK_ID, {
        label: 'Bedingung',
        category: blockCategories.teaser,
        media: `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill="currentColor" d="M3 5h18v2H3V5zm4 6h10v2H7v-2zm4 6h2v2h-2v-2z"/>
        </svg>`,
        content: {
            type: COMPONENT_TYPE,
            conditionField: defaultField,
            conditionOperator: DEFAULT_OPERATOR,
            conditionValue: '',
            attributes: {
                class: 'sio-teaser-condition',
                'data-siotype': COMPONENT_TYPE,
                'data-condition-field': defaultField,
                'data-condition-operator': DEFAULT_OPERATOR,
                'data-condition-value': '',
                'data-field-label': defaultLabel,
            },
        },
    });
}
